'use client'

import type { ProductFragment } from '@/lib/types/datocms/graphql/generated'
import { Tab, TabList, TabGroup, TabPanels, TabPanel } from '@headlessui/react'
import clsx from 'clsx'
import { Overview } from './Overview'
import { Gallery } from './Gallery'
import { Specifications } from './Specifications'
import { Information } from './Information'
import CtaLarge from '@/components/ui/cta/CtaLarge'
import FeaturesContentSection from '../sections/FeaturesContentSection'

type Props = {
	product: ProductFragment
}

export default function ProductTabs({ product }: Props) {
	const tabs = [
		{
			name: 'Overview',
		},
		{
			name: 'Gallery',
		},
		{
			name: 'Specifications',
		},
		{
			name: 'Standard Features',
		},
		{
			name: 'Upgrades',
		},
	]
	return (
		<TabGroup className="@container">
			<TabList className="border-b">
				<div className="flex justify-between items-center max-w-screen-2xl mx-auto common-x-padding overflow-x-auto scroll-hidden">
					<div className="flex">
						{tabs.map((tab) => (
							<Tab
								key={tab.name}
								className={({ selected }) =>
									clsx(
										'relative px-2 md:px-4 py-4 md:py-7 whitespace-nowrap',
										'outline-none ring-0 border-none',
										selected
											? 'bg-white text-blue after:absolute after:bottom-0 after:inset-x-2 md:after:inset-x-4 after:h-[2px] after:bg-blue'
											: 'text-gray-600 hover:bg-white hover:text-blue'
									)
								}
							>
								{tab.name}
							</Tab>
						))}
					</div>

					{product.tabCta && (
						<CtaLarge
							cta={product.tabCta}
							icon={{
								icon: 'Headset',
								orientation: 'left',
								size: 'sm',
							}}
							className="@5xl:w-[305px]"
						/>
					)}
				</div>
			</TabList>

			<TabPanels>
				{tabs.map((tab) => (
					<TabPanel key={tab.name} className={clsx(' bg-white')}>
						{tab.name === 'Overview' && (
							<Overview
								overview={product.overview}
								description={product.productDescription}
								cta={product.cta}
								isProductPage
							/>
						)}
						{tab.name === 'Gallery' && <Gallery gallery={product.gallery} />}
						{tab.name === 'Specifications' && (
							<Specifications specifications={product.specifications} />
						)}
						{tab.name === 'Standard Features' && product.highlights && (
							<FeaturesContentSection {...product.highlights} />
						)}
						{tab.name === 'Upgrades' && product.upgrades && (
							<FeaturesContentSection {...product.upgrades} />
						)}
					</TabPanel>
				))}
			</TabPanels>
		</TabGroup>
	)
}
